import { useEffect, useRef, useState } from 'react';

import { fetchEscalationHistory } from '@/api/project-details/api';
import { type EscalationHistory, mapEscalationHistoryDTO } from '@/models/escalation.model';
import { createLogger } from '@/utils/logging';

const logger = createLogger('useEscalationHistory', 'hook');

export interface EscalationHistoryState {
  history: EscalationHistory | null;
  loading: boolean;
  error: boolean;
}

export interface UseEscalationHistoryParams {
  accessToken: string | null | undefined;
  clientId: string;
  projectId: string;
  /** When false the fetch is skipped — use this to gate on permission resolution. */
  enabled?: boolean;
}

/** Fetch escalation notification history for the current cycle. */
export function useEscalationHistory({
  accessToken,
  clientId,
  projectId,
  enabled = true,
}: UseEscalationHistoryParams): EscalationHistoryState {
  const [state, setState] = useState<EscalationHistoryState>({
    history: null,
    loading: enabled,
    error: false,
  });

  // Tracks whether the effect is still current; prevents stale updates and
  // ensures rapid re-renders with the same params do not fire duplicate requests.
  const activeRef = useRef(true);

  useEffect(() => {
    activeRef.current = true;

    if (!enabled || !accessToken || !clientId || !projectId) {
      setState({ history: null, loading: false, error: false });
      return () => {
        activeRef.current = false;
      };
    }

    setState({ history: null, loading: true, error: false });

    void fetchEscalationHistory(accessToken, clientId, projectId)
      .then((dto) => {
        if (activeRef.current) {
          setState({ history: mapEscalationHistoryDTO(dto), loading: false, error: false });
        }
      })
      .catch((err: unknown) => {
        if (activeRef.current) {
          logger.error('Failed to load escalation history', err instanceof Error ? err : undefined);
          setState({ history: null, loading: false, error: true });
        }
      });

    return () => {
      activeRef.current = false;
    };
  }, [accessToken, clientId, projectId, enabled]);

  return state;
}
